import WorkingCapitalStrip from "../components/workcap/WorkingCapitalStrip";
import React from "react";
import { useNavigate } from "react-router-dom";
import { Thermometer, Wind } from "lucide-react";
import { BRANCHES, KPI_SNAPSHOT } from "../lib/data";
import { Tile, KPI } from "../components/Primitives";
import PhilippinesMap from "../components/PhilippinesMap";
import DecisionsTile from "../components/command/DecisionsTile";
import DemandForecastTile from "../components/command/DemandForecastTile";
import InventoryMixTile from "../components/command/InventoryMixTile";
import { RegionCoverTile, PilotAlertsTile } from "../components/command/RegionAlertsTiles";
import { StockoutRiskTile, AtRiskBranchesTile } from "../components/command/BranchRiskTiles";
import { OriginScope, OriginSummary, syn, fmtDate } from "../lib/origin.jsx";
import { useInventoryValuation } from "../lib/valuation";
import { useRole } from "../lib/role.jsx";
import { formatPeso, formatNumber, pct } from "../lib/utils";

export default function CommandCenter() {
  return (
    <OriginScope>
      <CommandCenterBody />
    </OriginScope>
  );
}

function CommandCenterBody() {
  const { role, canAccess, branchScope } = useRole();
  const navigate = useNavigate();
  const valuation = useInventoryValuation(branchScope?.branchId);
  const openBranch = (b) => { if (canAccess("/inventory")) navigate(`/inventory?branch=${b.id}`); };

  return (
    <div className="space-y-6 animate-fade-in">
      <Hero role={role} />
      <OriginSummary />
      <WorkingCapitalStrip />

      <div className="tile grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 divide-x divide-y md:divide-y-0 divide-border" data-testid="kpi-strip">
        <KPI testid="kpi-ending-inventory" label="Ending Inventory" value={formatPeso(valuation.total, { compact: true })}
             sub={`as of ${fmtDate(valuation.asOf)}`} status={valuation.stale ? "warning" : "success"} origin={valuation.originProps} />
        <KPI testid="kpi-turnover" label="Inventory Turnover" value={`${KPI_SNAPSHOT.inventoryTurnover.toFixed(1)}×`} sub="Target 8–12×" status="success" trend={0.6} origin={syn("COGS ÷ average inventory")} />
        <KPI testid="kpi-doh" label="Days on Hand" value={`${KPI_SNAPSHOT.daysSalesInventory}d`} sub="Healthy 20–60d" status="success" trend={-1.2} origin={syn("network days of supply")} />
        <KPI testid="kpi-stockout" label="Stockout SKUs" value={formatNumber(KPI_SNAPSHOT.stockoutSKUs)} sub="A-class rate 4.7%" status="warning" trend={-3.4} origin={syn("SKU × branch stock positions")} />
        <KPI testid="kpi-forecast" label="Forecast Accuracy" value={pct(KPI_SNAPSHOT.forecastAccuracy, 1)} sub="A-class weighted" status="success" trend={1.4} origin={syn("forecast vs actual demand")} />
        <KPI testid="kpi-otd" label="Supplier On-Time" value={pct(KPI_SNAPSHOT.supplierOTD, 1)} sub="Target >85%" status="warning" trend={-1.8} origin={syn("supplier receipts vs PO dates")} />
      </div>

      <div className="grid grid-cols-12 gap-6">
        <DecisionsTile branchId={branchScope?.branchId} />
        <DemandForecastTile />
        <InventoryMixTile valuation={valuation} />

        <Tile className="col-span-12 lg:col-span-7" testid="branch-map" title="Branch Network — Stock Health" origin={mapOrigin(valuation)}>
          <div className="h-[520px]">
            <PhilippinesMap colorBy="health" valueByBranch={valuation.valueByBranch} onSelect={openBranch} />
          </div>
        </Tile>
        <StockoutRiskTile valuation={valuation} onOpen={openBranch} />

        <RegionCoverTile valuation={valuation} />
        <PilotAlertsTile />
        <AtRiskBranchesTile valuation={valuation} onOpen={openBranch} />
      </div>
    </div>
  );
}

function mapOrigin(v) {
  if (v.origin === "synthetic") return syn("stock health, days on hand, stockout rate, inventory value");
  return {
    origin: "mixed", updatedAt: v.asOf,
    source: `Stock health: synthetic sample dataset · Inventory value: ${v.source}`,
    parts: [
      { origin: "actual", text: "Inventory value on hover for branches with a 1Rotary snapshot" },
      { origin: "synthetic", text: "Health status, days on hand, stockout rate" },
    ],
  };
}

function Hero({ role }) {
  return (
    <div className="tile grid-bg relative overflow-hidden">
      <div className="px-6 py-5 flex flex-wrap items-center gap-x-12 gap-y-4 relative">
        <img src="/1rotary-logo.png" alt="1Rotary" data-testid="hero-logo"
             className="h-80 w-auto -my-8 -ml-2 shrink-0 object-contain drop-shadow-2xl" />
        <div className="flex-1 min-w-[280px]">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-primary font-semibold mb-1.5">
            <span className="live-dot" />
            Inventory &amp; Demand Command Center
            <span className="text-subtle">·</span>
            <span className="text-muted">{role.short} View</span>
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight leading-tight">
            Cooling the Nation, <span className="italic text-primary">Creating Opportunities.</span>
          </h1>
          <p data-testid="hero-subtitle" className="text-sm text-muted mt-2 max-w-xl leading-relaxed">
            Stock health, ending inventory value and demand signals across {BRANCHES.length} branches — scored daily by seven inventory engines, with every figure labelled Actual, Synthetic or Mixed.
          </p>
        </div>
        <div className="flex items-center gap-8 divider-x">
          <div className="pl-0">
            <div className="kpi-label">PH Time</div>
            <div className="font-mono-tabular text-2xl font-semibold mt-1">
              {new Date().toLocaleTimeString("en-PH", { hour: "2-digit", minute: "2-digit", hour12: false })}
            </div>
          </div>
          <div className="pl-8">
            <div className="kpi-label flex items-center gap-1.5"><Thermometer size={11} /> NCR Heat Index</div>
            <div className="font-mono-tabular text-2xl font-semibold mt-1 text-critical">42°C</div>
          </div>
          <div className="pl-8">
            <div className="kpi-label flex items-center gap-1.5"><Wind size={11} /> PAGASA</div>
            <div className="font-mono-tabular text-sm mt-1.5 text-warning">TD Auring · E. Visayas</div>
          </div>
        </div>
      </div>
    </div>
  );
}
